/**
 * Package detail component for viewing package information.
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Descriptions, Tag, Button, Space, Card, Typography, Divider, Progress, message, Tooltip } from 'antd';
import {
  DownloadOutlined,
  CopyOutlined,
  EditOutlined,
  HistoryOutlined,
  FileOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { BSPPackageDetail as BSPPackageDetailType, FileType, OplIssueListItem, PackageFile, PackageRequirement } from '@/types';
import { formatDate, formatFileSize, formatUploadMode, formatChunkSize, copyToClipboard } from '@/utils';
import { resolveUploadPercent } from '@/utils/uploadProgress';
import { downloadFile, uploadFile, getShareableDownloadLink } from '@/services/obsService';
import { completePackageFileUpload, failPackageFileUpload, heartbeatPackageFile, patchPackage, startPackageFileUpload, updatePackageUploadStatus } from '@/api/packages';
import { CopyExternalLinkButton } from '@/components/packages/CopyExternalLinkButton';
import { getPackageRequirements } from '@/api/requirements';
import { getPackageIssues } from '@/api/opl';
import { listUploadRecords, updateUploadRecord } from '@/api/uploadRecords';
import type { UploadedFileItem } from '@/api/uploadRecords';
import { reportRetryUploadRecord } from '@/utils/uploadRecordUtils';
import IssueStatusTag from '@/components/opl/IssueStatusTag';
import PriorityTag from '@/components/opl/PriorityTag';
import { FeishuRichText } from '@/components/common';
import { useFeishuTitles } from '@/hooks/useFeishuTitles';
import { useUploadProgressStore } from '@/store/uploadProgressStore';

const { Text, Title } = Typography;

// 数组是权威值；老数据只有单值时兜底
const formatVehicle = (list?: string[], single?: string) => {
  const values = list?.length ? list : single ? [single] : [];
  return values.length ? values.join('、') : '-';
};

const formatSpeed = (bps: number) =>
  bps > 1024 * 1024
    ? `${(bps / 1024 / 1024).toFixed(1)} MB/s`
    : `${(bps / 1024).toFixed(0)} KB/s`;

const formatUploadTiming = (startedAt: string, finishedAt: string, avgSpeedMbps?: number | null): string => {
  const fmt = (d: Date) =>
    `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  const start = new Date(startedAt);
  const end = new Date(finishedAt);
  const durationSec = Math.round((end.getTime() - start.getTime()) / 1000);
  const speed = avgSpeedMbps != null ? `, ${avgSpeedMbps.toFixed(1)}MB/s` : '';
  return `${fmt(start)}~${fmt(end)}, ${durationSec}s${speed}`;
};

interface PackageDetailProps {
  package: BSPPackageDetailType;
  allowedFileTypes?: FileType[];
  onEdit?: () => void;
  onViewLogs?: () => void;
  onClose?: () => void;
  onRefresh?: () => void;
}

export const PackageDetail: React.FC<PackageDetailProps> = ({
  package: pkg,
  allowedFileTypes,
  onEdit,
  onViewLogs,
  onClose,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [linkedReqs, setLinkedReqs] = useState<PackageRequirement[]>([]);
  const [linkedIssues, setLinkedIssues] = useState<OplIssueListItem[]>([]);
  const { progress, setProgress, incrementManualRetry, incrementAutoRetry } = useUploadProgressStore();

  // 文档「内容」字段里可能内嵌飞书链接，解析标题后展示
  const { titles: feishuTitles, loading: feishuLoading } = useFeishuTitles([
    pkg.release_note_content,
    pkg.test_report_content,
  ]);

  // Map from fileId to the retry input ref
  const retryInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  // Retry input refs for doc files
  const rnRetryInputRef = useRef<HTMLInputElement | null>(null);
  const trRetryInputRef = useRef<HTMLInputElement | null>(null);
  // Track previous doc upload statuses to detect done transition
  const prevDocStatus = useRef<Record<string, string>>({});
  // Upload record ID for reporting retry status to upload board
  const uploadRecordIdRef = useRef<number | null>(null);
  // Upload record ID for the current retry operation (separate from main upload record)
  const retryRecordIdRef = useRef<number | null>(null);

  useEffect(() => {
    getPackageRequirements(pkg.id)
      .then(setLinkedReqs)
      .catch(() => {});
    getPackageIssues(pkg.id)
      .then(setLinkedIssues)
      .catch(() => {});
    // Fetch upload record ID for retry reporting
    listUploadRecords({ deliverable_type: 'package', deliverable_id: pkg.id, page_size: 1 })
      .then(res => {
        if (res.results?.length > 0) uploadRecordIdRef.current = res.results[0].id;
      })
      .catch(() => {});
  }, [pkg.id]);

  // Report all current files to upload board (called after retry uploads complete/fail)
  const reportRetryToUploadBoard = (): Promise<void> => {
    if (!uploadRecordIdRef.current) return Promise.resolve();
    const store = useUploadProgressStore.getState();
    const allFiles: UploadedFileItem[] = [];
    const deriveFileStatus = (storeKey: string, serverStatus: string | undefined): string => {
      const s = store.getProgress(storeKey)?.status;
      return s || serverStatus || 'done';
    };
    let allDone = true;
    let anyFailed = false;

    (pkg.files || []).forEach(f => {
      const key = `package-${pkg.id}-file-${f.id}`;
      const st = deriveFileStatus(key, f.upload_status);
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const e = store.getProgress(key);
      allFiles.push({
        file_name: e?.rawFile?.name ?? f.file_name,
        file_size: e?.rawFile?.size ?? f.file_size,
        file_type_name: f.file_type?.name || '',
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
      });
    });

    // Doc files
    const trPrefix = `package-${pkg.id}-doc-test_report`;
    const trKeys = Object.keys(store.progress).filter((k) => k.startsWith(`${trPrefix}-`));
    const trSource = pkg.test_report_files?.length
      ? pkg.test_report_files
      : pkg.test_report_url || pkg.test_report_name
        ? [{ name: pkg.test_report_name || '', size: pkg.test_report_size || 0 }]
        : [];
    const docEntries: { name: string; key: string; realName: string; realSize: number }[] = [
      { name: 'Release Note', key: `package-${pkg.id}-doc-release_note`, realName: pkg.release_note_name || '', realSize: pkg.release_note_size || 0 },
      ...(trKeys.length > 0
        ? trKeys.map((key, index) => ({
            name: 'Test Report',
            key,
            realName: trSource[index]?.name || pkg.test_report_name || '',
            realSize: trSource[index]?.size || pkg.test_report_size || 0,
          }))
        : [{ name: 'Test Report', key: trPrefix, realName: pkg.test_report_name || '', realSize: pkg.test_report_size || 0 }]),
    ];
    docEntries.forEach(({ name, key, realName, realSize }) => {
      const st = store.getProgress(key)?.status;
      if (!st) return;
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const e = store.getProgress(key);
      allFiles.push({
        file_name: e?.rawFile?.name ?? (realName || name),
        file_size: e?.rawFile?.size ?? realSize,
        file_type_name: name,
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
      });
    });

    const status = anyFailed ? 'failed' : allDone ? 'done' : 'uploading';
    return updateUploadRecord(uploadRecordIdRef.current, {
      upload_status: status,
      finished_at: status !== 'uploading' ? new Date().toISOString() : undefined,
      uploaded_files: allFiles,
    }).then(() => {}).catch(() => {});
  };

  // Update the current retry record (retryRecordIdRef) with latest file statuses
  const updateRetryRecord = (retryingFileKey: string): Promise<void> => {
    if (!retryRecordIdRef.current) return Promise.resolve();
    const store = useUploadProgressStore.getState();
    const allFiles: UploadedFileItem[] = [];
    let allDone = true;
    let anyFailed = false;

    (pkg.files || []).forEach(f => {
      const k = `package-${pkg.id}-file-${f.id}`;
      const e = store.getProgress(k);
      const st = e?.status ?? f.upload_status ?? 'done';
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const isRetrying = k === retryingFileKey;
      allFiles.push({
        file_name: e?.rawFile?.name ?? f.file_name,
        file_size: e?.rawFile?.size ?? f.file_size,
        file_type_name: f.file_type?.name || '',
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
        change_type: isRetrying ? (st === 'failed' ? 'failed' : 'added') : (st === 'failed' ? 'failed' : 'existing'),
      });
    });

    const trPrefix = `package-${pkg.id}-doc-test_report`;
    const trKeys = Object.keys(store.progress).filter((k) => k.startsWith(`${trPrefix}-`));
    const trSource = pkg.test_report_files?.length
      ? pkg.test_report_files
      : pkg.test_report_url || pkg.test_report_name
        ? [{ name: pkg.test_report_name || '', size: pkg.test_report_size || 0 }]
        : [];
    const docEntries: { name: string; key: string; realName: string; realSize: number }[] = [
      { name: 'Release Note', key: `package-${pkg.id}-doc-release_note`, realName: pkg.release_note_name || '', realSize: pkg.release_note_size || 0 },
      ...(trKeys.length > 0
        ? trKeys.map((key, index) => ({
            name: 'Test Report',
            key,
            realName: trSource[index]?.name || pkg.test_report_name || '',
            realSize: trSource[index]?.size || pkg.test_report_size || 0,
          }))
        : [{ name: 'Test Report', key: trPrefix, realName: pkg.test_report_name || '', realSize: pkg.test_report_size || 0 }]),
    ];
    docEntries.forEach(({ name, key, realName, realSize }) => {
      const e = store.getProgress(key);
      if (!e) return;
      const st = e.status ?? 'done';
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const isRetrying = key === retryingFileKey;
      allFiles.push({
        file_name: e?.rawFile?.name ?? (realName || name),
        file_size: e?.rawFile?.size ?? realSize,
        file_type_name: name,
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
        change_type: isRetrying ? (st === 'failed' ? 'failed' : 'added') : (st === 'failed' ? 'failed' : 'existing'),
      });
    });

    const uploadStatus = anyFailed ? 'failed' : allDone ? 'done' : 'uploading';
    return updateUploadRecord(retryRecordIdRef.current, {
      upload_status: uploadStatus,
      finished_at: uploadStatus !== 'uploading' ? new Date().toISOString() : undefined,
      uploaded_files: allFiles,
    }).then(() => {}).catch(() => {});
  };
  // Auto-refresh when doc file upload completes so pkg prop gets updated URL
  useEffect(() => {
    const docKeys = [
      `package-${pkg.id}-doc-release_note`,
      `package-${pkg.id}-doc-test_report`,
    ];
    let needRefresh = false;
    for (const key of docKeys) {
      const cur = progress[key]?.status;
      const prev = prevDocStatus.current[key];
      if (prev && prev !== 'done' && cur === 'done') {
        needRefresh = true;
      }
      if (cur !== undefined) prevDocStatus.current[key] = cur;
    }
    if (needRefresh) onRefresh?.();
  }, [progress, pkg.id, onRefresh]);

  const handleDownload = async (fileUrl: string, fileName: string) => {
    await downloadFile(fileUrl, fileName);
  };

  const handleCopyLink = async (fileUrl: string, fileName: string) => {
    try {
      const link = await getShareableDownloadLink(fileUrl, fileName);
      await copyToClipboard(link);
      message.success('下载链接已复制到剪贴板');
    } catch {
      message.error('获取下载链接失败');
    }
  };

  // Artifact 链接由后端解析（EP all_tar_url 或用户直接填写的下载链接），不能走 OBS 下载
  const handleArtifactDownload = (url?: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleArtifactCopyLink = async (url?: string | null) => {
    if (!url) return;
    try {
      await copyToClipboard(url);
      message.success('下载链接已复制到剪贴板');
    } catch {
      message.error('复制下载链接失败');
    }
  };

  const handleRetry = async (file: PackageFile, selectedFile: File) => {
    const key = `package-${pkg.id}-file-${file.id}`;
    incrementManualRetry(key);
    const startedAt = new Date().toISOString();
    setProgress(key, { percent: 0, status: 'uploading', rawFile: selectedFile, uploadStartedAt: startedAt, uploadFinishedAt: null, avgSpeedMbps: null });
    await updatePackageUploadStatus(pkg.id, 'uploading').catch(() => {});
    await startPackageFileUpload(pkg.id, file.id, 'uploading').catch(() => {});

    // 创建重传独立记录
    const store = useUploadProgressStore.getState();
    const initialFiles: UploadedFileItem[] = (pkg.files || []).map(f => {
      const k = `package-${pkg.id}-file-${f.id}`;
      const e = store.getProgress(k);
      const isTarget = f.id === file.id;
      return {
        file_name: isTarget ? selectedFile.name : (e?.rawFile?.name ?? f.file_name),
        file_size: isTarget ? selectedFile.size : (e?.rawFile?.size ?? f.file_size),
        file_type_name: f.file_type?.name || '',
        change_type: isTarget ? 'added' : ((e?.status === 'failed' || f.upload_status === 'failed') ? 'failed' : 'existing'),
      };
    });
    retryRecordIdRef.current = await reportRetryUploadRecord(
      'package', pkg.id, pkg.project.id,
      { name: pkg.name, vehicleModel: pkg.vehicle_model, description: pkg.description || '' },
      initialFiles, startedAt,
    );

    const startTime = Date.now();
    // 心跳：重传路径同样需要刷新 updated_at 并上报进度，
    // 否则服务端 upload_percent 会一直停留在重传前的残值上
    let lastHeartbeatPercent = 0;
    let currentPercent = 0;
    const heartbeatTimer = setInterval(() => {
      lastHeartbeatPercent = currentPercent;
      heartbeatPackageFile(pkg.id, file.id, currentPercent).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(selectedFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: selectedFile });
        currentPercent = p.percent;
        if (p.percent > 0 && p.percent - lastHeartbeatPercent >= 5) {
          lastHeartbeatPercent = p.percent;
          heartbeatPackageFile(pkg.id, file.id, p.percent).catch(() => {});
        }
      }, undefined, () => { incrementAutoRetry(key); }, key);
      const isUploadType = !file.source_type || file.source_type === 'upload';
      if (isUploadType && !result.md5) {
        console.error(`[PackageDetail] MD5 empty for ${file.file_name}, marking as failed`);
        setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
        await failPackageFileUpload(pkg.id, file.id).catch(() => {});
        reportRetryToUploadBoard();
        updateRetryRecord(key);
        return;
      }
      const finishedAt = new Date().toISOString();
      const durationSec = (Date.now() - startTime) / 1000;
      const avgSpeedMbps = durationSec > 0 ? parseFloat((result.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
      setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
      await reportRetryToUploadBoard();
      await updateRetryRecord(key);
      const startedAt = useUploadProgressStore.getState().getProgress(key)?.uploadStartedAt ?? null;
      await completePackageFileUpload(pkg.id, file.id, {
        file_url: result.fileUrl,
        object_key: result.objectKey,
        file_size: result.fileSize,
        checksum: result.md5 || '',
        file_name: selectedFile.name,
        upload_started_at: startedAt,
        upload_finished_at: finishedAt,
        avg_speed_mbps: avgSpeedMbps,
      });
      onRefresh?.();
    } catch {
      setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
      await failPackageFileUpload(pkg.id, file.id).catch(() => {});
      reportRetryToUploadBoard();
      updateRetryRecord(key);
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  const handleDocRetry = async (docType: 'release_note' | 'test_report', selectedFile: File, trIndex = -1) => {
    const key = docType === 'test_report'
      ? `package-${pkg.id}-doc-test_report-${trIndex >= 0 ? trIndex : 0}`
      : `package-${pkg.id}-doc-${docType}`;
    incrementManualRetry(key);
    const startedAt = new Date().toISOString();
    setProgress(key, { percent: 0, status: 'uploading', rawFile: selectedFile, uploadStartedAt: startedAt, uploadFinishedAt: null, avgSpeedMbps: null });
    await updatePackageUploadStatus(pkg.id, 'uploading').catch(() => {});

    // 创建重传独立记录
    const docTypeName = docType === 'release_note' ? 'Release Note' : 'Test Report';
    const store = useUploadProgressStore.getState();
    const initialFiles: UploadedFileItem[] = (pkg.files || []).map(f => {
      const k = `package-${pkg.id}-file-${f.id}`;
      const e = store.getProgress(k);
      return {
        file_name: e?.rawFile?.name ?? f.file_name,
        file_size: e?.rawFile?.size ?? f.file_size,
        file_type_name: f.file_type?.name || '',
        change_type: (e?.status === 'failed' || f.upload_status === 'failed') ? 'failed' : 'existing',
      };
    });
    initialFiles.push({
      file_name: selectedFile.name,
      file_size: selectedFile.size,
      file_type_name: docTypeName,
      change_type: 'added',
    });
    retryRecordIdRef.current = await reportRetryUploadRecord(
      'package', pkg.id, pkg.project.id,
      { name: pkg.name, vehicleModel: pkg.vehicle_model, description: pkg.description || '' },
      initialFiles, startedAt,
    );

    const startTime = Date.now();
    // 心跳：刷新 package.updated_at，防止其他 Tab 把正在进行的文档重传误判为卡住
    const heartbeatTimer = setInterval(() => {
      patchPackage(pkg.id, {}).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(selectedFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: selectedFile });
      }, undefined, () => { incrementAutoRetry(key); }, key);
      const finishedAt = new Date().toISOString();
      const durationSec = (Date.now() - startTime) / 1000;
      const avgSpeedMbps = durationSec > 0 ? parseFloat((result.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
      setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
      await reportRetryToUploadBoard();
      await updateRetryRecord(key);
      const uploadStartedAt = useUploadProgressStore.getState().getProgress(key)?.uploadStartedAt ?? new Date(Date.now() - durationSec * 1000).toISOString();
      const docTiming = {
        ...pkg.doc_upload_timing,
        [docType]: { started_at: uploadStartedAt, finished_at: finishedAt, avg_speed_mbps: avgSpeedMbps },
      };
      if (docType === 'test_report') {
        const base = pkg.test_report_files?.length
          ? [...pkg.test_report_files]
          : (pkg.test_report_url || pkg.test_report_name)
            ? [{
                url: pkg.test_report_url || '',
                name: pkg.test_report_name || '',
                size: pkg.test_report_size || 0,
                checksum: pkg.test_report_checksum || '',
              }]
            : [];
        const index = trIndex >= 0 ? trIndex : base.findIndex(d => !d.url);
        const entry = {
          url: result.fileUrl,
          name: selectedFile.name,
          size: result.fileSize,
          checksum: result.md5 || '',
        };
        if (index >= 0 && index < base.length) base[index] = entry;
        else base.push(entry);
        await patchPackage(pkg.id, {
          test_report_files: base,
          doc_files_merge: true,
          version: pkg.version,
          doc_upload_timing: docTiming,
        });
      } else {
        await patchPackage(pkg.id, {
          [`${docType}_url`]: result.fileUrl,
          [`${docType}_name`]: selectedFile.name,
          [`${docType}_size`]: result.fileSize,
          [`${docType}_checksum`]: result.md5 || '',
          version: pkg.version,
          doc_upload_timing: docTiming,
        });
      }
      onRefresh?.();
    } catch {
      setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
      reportRetryToUploadBoard();
      updateRetryRecord(key);
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  // 供应商固定文件（向后兼容：无 file_source 的旧数据归入供应商）
  const fileTypeOrderMap = (allowedFileTypes || []).reduce<Record<number, number>>(
    (acc, ft, idx) => ({ ...acc, [ft.id]: idx }), {}
  );
  const supplierFixedFiles = (pkg.files || [])
    .filter(f => f.file_source === 'supplier' || !f.file_source)
    .sort((a, b) =>
      (fileTypeOrderMap[a.file_type.id] ?? 999) - (fileTypeOrderMap[b.file_type.id] ?? 999)
    );

  const isMd5Missing = (file: PackageFile): boolean =>
    file.source_type === 'upload' && !file.checksum && (!!file.file_url || file.upload_status === 'done');

  // 渲染上传状态区块（进度条 / 失败标签 / 重试）
  const renderUploadStatus = (file: PackageFile) => {
    const key = `package-${pkg.id}-file-${file.id}`;
    const storeEntry = progress[key];
    const effectiveStatus = storeEntry?.status ?? file.upload_status;

    if (!effectiveStatus || effectiveStatus === 'done') {
      if (isMd5Missing(file)) {
        return (
          <Space size={4} style={{ marginTop: 4 }}>
            <Tag color="error">上传失败</Tag>
            <input
              type="file"
              style={{ display: 'none' }}
              ref={(el) => { retryInputRefs.current[file.id] = el; }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleRetry(file, f);
                e.target.value = '';
              }}
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => retryInputRefs.current[file.id]?.click()}
            >
              选择文件重试
            </Button>
          </Space>
        );
      }
      return null;
    }

    if (effectiveStatus === 'uploading') {
      // 优先用本 Tab 的实时进度；跨电脑/跨会话时 storeEntry 为空，回退到服务端持久化的 upload_percent
      const pct = resolveUploadPercent(file, storeEntry);
      const speed = storeEntry?.currentSpeed;
      return (
        <Space orientation="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
          <Progress percent={pct} size="small" status="active" style={{ marginBottom: 0 }} />
          {speed !== undefined && speed > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(speed)}</Text>
          )}
        </Space>
      );
    }

    if (effectiveStatus === 'failed') {
      const savedFile = storeEntry?.rawFile;
      return (
        <Space size={4} style={{ marginTop: 4 }}>
          <Tag color="error">上传失败</Tag>
          {savedFile ? (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>{savedFile.name}</Text>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(file, savedFile)}
              >
                重试
              </Button>
            </>
          ) : (
            <>
              <input
                type="file"
                style={{ display: 'none' }}
                ref={(el) => { retryInputRefs.current[file.id] = el; }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleRetry(file, f);
                  e.target.value = '';
                }}
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => retryInputRefs.current[file.id]?.click()}
              >
                选择文件重试
              </Button>
            </>
          )}
        </Space>
      );
    }

    if (effectiveStatus === 'queued') {
      return <Tag color="processing" style={{ marginTop: 4 }}>排队中</Tag>;
    }

    return null;
  };

  // 渲染单个固定文件项（供应商/Momenta 复用）
  const renderFixedFileItem = (file: PackageFile) => {
    const isGitSource = file.source_type === 'git';
    const isArtifactSource = file.source_type === 'artifact';
    const isSelectSource = file.source_type === 'select';
    const key = `package-${pkg.id}-file-${file.id}`;
    const storeEntry = progress[key];
    const effectiveStatus = storeEntry?.status ?? file.upload_status;
    const canDownload = !isGitSource && !isArtifactSource && (!effectiveStatus || effectiveStatus === 'done') && !isMd5Missing(file);

    return (
      <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <FileOutlined className="text-2xl text-blue-500" />
            <div>
              <div className="font-medium">
                {isGitSource ? `${file.file_type.name} (Git源)` : isArtifactSource ? `${file.file_type.name} (Artifact)` : (storeEntry?.rawFile?.name ?? file.file_name)}
              </div>
              <div className="text-sm text-gray-600">
                <Space orientation="vertical" size={4}>
                  <div>
                    <Tag>{file.file_type.name}</Tag>
                    {!isGitSource && !isArtifactSource && file.file_size > 0 && (
                      <Text type="secondary" className="ml-2">{formatFileSize(file.file_size)}</Text>
                    )}
                    {!isGitSource && !isArtifactSource && file.upload_started_at && file.upload_finished_at && (
                      <Text type="secondary" className="ml-2" style={{ fontSize: '12px' }}>
                        {formatUploadTiming(file.upload_started_at, file.upload_finished_at, file.avg_speed_mbps)}
                      </Text>
                    )}
                    {isGitSource && <Tag color="cyan">Git源</Tag>}
                    {isArtifactSource && <Tag color="purple">Artifact</Tag>}
                    {isSelectSource && <Tag color="orange">选择已有</Tag>}
                  </div>
                  {isGitSource && file.git_url && (
                    <>
                      <div>
                        <Text type="secondary">Git仓库:</Text>
                        <Text copyable className="ml-2" style={{ fontSize: '12px' }}>{file.git_url}</Text>
                      </div>
                      <div>
                        <Text type="secondary">分支:</Text>
                        <Text code className="ml-2">{file.git_branch}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Commit:</Text>
                        <Text code copyable className="ml-2">{file.git_commit}</Text>
                      </div>
                      {file.git_tag && (
                        <div>
                          <Text type="secondary">Tag:</Text>
                          <Text code className="ml-2">{file.git_tag}</Text>
                        </div>
                      )}
                    </>
                  )}
                  {isArtifactSource && file.artifact_name && (
                    <div>
                      <Text type="secondary">产物包名称:</Text>
                      <Text copyable className="ml-2">{file.artifact_name}</Text>
                    </div>
                  )}
                  {!isGitSource && !isArtifactSource && file.checksum && (
                    <Text type="secondary" copyable={{ text: file.checksum }} title={file.checksum} style={{ fontSize: '12px' }}>
                      MD5: {file.checksum}
                    </Text>
                  )}
                  {file.remark && (
                    <div>
                      <Text type="secondary">备注:</Text>
                      <Text className="ml-2" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{file.remark}</Text>
                    </div>
                  )}
                  {!isGitSource && !isArtifactSource && renderUploadStatus(file)}
                </Space>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isArtifactSource ? (
            <Tooltip title={file.artifact_download_url ? undefined : '未查询到产物包下载链接'}>
              <span>
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  disabled={!file.artifact_download_url}
                  onClick={() => handleArtifactDownload(file.artifact_download_url)}
                >
                  下载
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  disabled={!file.artifact_download_url}
                  onClick={() => handleArtifactCopyLink(file.artifact_download_url)}
                >
                  复制下载链接
                </Button>
              </span>
            </Tooltip>
          ) : !isGitSource && (
            <>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                disabled={!canDownload}
                onClick={() => canDownload && handleDownload(file.file_url, file.file_name)}
              >
                下载
              </Button>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                disabled={!canDownload}
                onClick={() => canDownload && handleCopyLink(file.file_url, file.file_name)}
              >
                复制下载链接
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  // 渲染文档文件区块（Release Note / 测试报告，供应商/Momenta 复用）
  type DocFileEntry = { name?: string; url?: string; size?: number; checksum?: string };
  const renderDocSection = (
    files: DocFileEntry[],
    content: string | undefined,
    label: string,
    tagColor: string,
    timing?: { started_at: string; finished_at: string; avg_speed_mbps?: number | null },
  ) => {
    if (files.length === 0 && !content) return null;
    const dataSource = files;
    return (
      <div className="mb-4">
        <div className="font-medium mb-2">{label}</div>
        {dataSource.length > 0 && (
          <div className={content ? 'mb-4' : ''}>
            {content && <div className="text-gray-600 text-sm mb-1">附件:</div>}
            <div>
              {dataSource.map((file, idx) => (
                <div key={idx} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <FileOutlined className="text-2xl text-green-500" />
                      <div>
                        <div className="font-medium">{file.name}</div>
                        <div className="text-sm text-gray-600">
                          <Space orientation="vertical" size={4}>
                            <Space>
                              <Tag color={tagColor}>{label}</Tag>
                              {file.size && <Text type="secondary" className="text-xs">{formatFileSize(file.size)}</Text>}
                              {idx === 0 && timing?.started_at && timing?.finished_at && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {formatUploadTiming(timing.started_at, timing.finished_at, timing.avg_speed_mbps)}
                                </Text>
                              )}
                            </Space>
                            {file.checksum && (
                              <Text type="secondary" copyable={{ text: file.checksum }} style={{ fontSize: '12px' }}>
                                MD5: {file.checksum}
                              </Text>
                            )}
                          </Space>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownload(file.url!, file.name!)}>
                      下载
                    </Button>
                    <Button type="link" icon={<CopyOutlined />} onClick={() => handleCopyLink(file.url!, file.name!)}>
                      复制下载链接
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {content && (
          <div>
            {files.length > 0 && <div className="text-gray-600 text-sm mb-1">内容:</div>}
            <FeishuRichText
              content={content}
              titles={feishuTitles}
              loading={feishuLoading}
              className="whitespace-pre-wrap p-4 bg-gray-50 rounded"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="package-detail">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-1">
          <Title level={4} className="mb-0">
            {pkg.name}
          </Title>
          <CopyExternalLinkButton id={pkg.id} projectId={pkg.project?.id} />
        </div>
        <Space>
          <Button icon={<HistoryOutlined />} onClick={onViewLogs}>
            操作日志
          </Button>
          <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
            编辑
          </Button>
        </Space>
      </div>

      <Card className="mb-4">
        <Descriptions title="基本信息" column={2} bordered size="small">
          <Descriptions.Item label="包名称">{pkg.name}</Descriptions.Item>
          <Descriptions.Item label="版本号">{pkg.version}</Descriptions.Item>
          <Descriptions.Item label="项目">
            <Tag color="blue">{pkg.project?.name}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="车型">{formatVehicle(pkg.vehicle_models, pkg.vehicle_model)}</Descriptions.Item>
          <Descriptions.Item label="子车型">{formatVehicle(pkg.vehicle_subtypes, pkg.vehicle_subtype)}</Descriptions.Item>
          <Descriptions.Item label="硬件平台">{formatVehicle(pkg.platforms, pkg.platform)}</Descriptions.Item>
          <Descriptions.Item label="说明">{pkg.description || '-'}</Descriptions.Item>
          <Descriptions.Item label="上传者">
            {pkg.created_by?.username}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {formatDate(pkg.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {formatDate(pkg.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label="上传模式">
            {formatUploadMode(pkg.upload_mode)}
          </Descriptions.Item>
          <Descriptions.Item label="分片大小">
            {formatChunkSize(pkg.chunk_size)}
          </Descriptions.Item>
          <Descriptions.Item label="关联文件" span={2}>
            {pkg.external_releases?.length ? (
              <Space size={[4, 4]} wrap>
                {pkg.external_releases.map((r) => (
                  <Tag key={r.id} color="purple">{r.name}</Tag>
                ))}
              </Space>
            ) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 底软供应商上传文件 */}
      <Card title="底软供应商上传文件" className="mb-4">
        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          <span style={{ fontWeight: 'bold' }}>固定文件</span>
        </Divider>
        {supplierFixedFiles.length > 0
          ? <div>{supplierFixedFiles.map((file) => <div key={file.id}>{renderFixedFileItem(file)}</div>)}</div>
          : <Text type="secondary">暂无固定文件</Text>}
        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          <span style={{ fontWeight: 'bold' }}>文档文件</span>
        </Divider>
        {(() => {
          const rnKey = `package-${pkg.id}-doc-release_note`;
          const rnEntry = progress[rnKey];
          // store 无记录时从字段推导：upload_status=failed = 刷新后上传中断
          const rnStuck = !rnEntry && pkg.release_note_upload_status === 'failed';
          // store 无记录时从字段推导：name 有值但 url 为空 = 跨标签页/刷新后仍在上传中
          const rnInProgress = !rnEntry && !rnStuck && !!pkg.release_note_name && !pkg.release_note_url;
          if ((rnEntry && rnEntry.status !== 'done') || rnStuck || rnInProgress) {
            const rnStatus = rnStuck ? 'failed' : rnInProgress ? 'uploading' : rnEntry!.status;
            const rnPercent = (rnStuck || rnInProgress) ? 0 : rnEntry!.percent;
            const rnFileName = rnEntry?.rawFile?.name ?? ((rnStuck || rnInProgress) ? pkg.release_note_name : undefined);
            return (
              <div className="mb-4">
                <div className="font-medium mb-2">Release Note</div>
                <Space orientation="vertical" size={2} style={{ maxWidth: 400 }}>
                  {rnFileName && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{rnFileName}</Text>
                  )}
                  {rnStatus === 'queued' && (
                    <Tag color="processing">排队中</Tag>
                  )}
                  {rnStatus !== 'failed' && rnStatus !== 'queued' && (
                    <Progress
                      percent={rnPercent}
                      size="small"
                      status="active"
                      style={{ marginBottom: 0 }}
                    />
                  )}
                  {rnStatus === 'failed' && (
                    <>
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        ref={rnRetryInputRef}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleDocRetry('release_note', f);
                          e.target.value = '';
                        }}
                      />
                      <Space size={4}>
                        <Tag color="error">上传失败</Tag>
                        {rnEntry?.rawFile && !rnStuck ? (
                          <Button size="small" icon={<ReloadOutlined />} onClick={() => handleDocRetry('release_note', rnEntry!.rawFile!)}>重试</Button>
                        ) : (
                          <Button size="small" icon={<ReloadOutlined />} onClick={() => rnRetryInputRef.current?.click()}>选择文件重试</Button>
                        )}
                      </Space>
                    </>
                  )}
                  {rnEntry && rnStatus !== 'failed' && rnEntry.currentSpeed !== undefined && rnEntry.currentSpeed > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(rnEntry.currentSpeed)}</Text>
                  )}
                  {rnStatus === 'uploading' && (!rnEntry?.currentSpeed || rnEntry?.currentSpeed === 0) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>上传中...</Text>
                  )}
                </Space>
                {pkg.release_note_content && (
                  <div className="mt-3">
                    <div className="text-gray-600 text-sm mb-1">内容:</div>
                    <FeishuRichText
                      content={pkg.release_note_content}
                      titles={feishuTitles}
                      loading={feishuLoading}
                      className="whitespace-pre-wrap p-4 bg-gray-50 rounded"
                    />
                  </div>
                )}
              </div>
            );
          }
          return renderDocSection(
            pkg.release_note_url
              ? [{ name: pkg.release_note_name, url: pkg.release_note_url, size: pkg.release_note_size, checksum: pkg.release_note_checksum }]
              : [],
            pkg.release_note_content,
            'Release Note',
            'green',
            pkg.doc_upload_timing?.release_note,
          );
        })()}
        {(() => {
          const prefix = `package-${pkg.id}-doc-test_report`;
          const allTrDocs = pkg.test_report_files?.length
            ? pkg.test_report_files.map(d => ({ name: d.name, url: d.url, size: d.size, checksum: d.checksum }))
            : (pkg.test_report_url || pkg.test_report_name)
              ? [{ name: pkg.test_report_name, url: pkg.test_report_url, size: pkg.test_report_size, checksum: pkg.test_report_checksum }]
              : [];
          const trDocs = allTrDocs.filter(d => d.url);
          // 未回写 url 的附件 + 本标签页活跃进度
          const pendingRows = new Map<string, string | undefined>();
          allTrDocs.forEach((d, index) => {
            if (!d.url) pendingRows.set(`${prefix}-${index}`, d.name);
          });
          Object.keys(progress).forEach((k) => {
            if (k !== prefix && !k.startsWith(`${prefix}-`)) return;
            if (!progress[k] || progress[k].status === 'done') return;
            if (!pendingRows.has(k)) pendingRows.set(k, undefined);
          });
          const legacyStuck = pendingRows.size === 0 && pkg.test_report_upload_status === 'failed';
          const content = pkg.test_report_content;
          if (!trDocs.length && pendingRows.size === 0 && !legacyStuck && !content) return null;

          const renderProgress = (key: string, name: string | undefined, failed: boolean, rawFile?: File) => {
            const entry = progress[key];
            const status = entry?.status ?? (failed ? 'failed' : 'uploading');
            const retry = (file: File) => {
              const index = key === prefix ? -1 : Number(key.slice(prefix.length + 1));
              handleDocRetry('test_report', file, Number.isInteger(index) ? index : -1);
            };
            return (
              <div key={key} className="mb-2">
                <Space orientation="vertical" size={2} style={{ maxWidth: 400 }}>
                  {name && <Text type="secondary" style={{ fontSize: 12 }}>{name}</Text>}
                  {status === 'queued' && <Tag color="processing">排队中</Tag>}
                  {status !== 'failed' && status !== 'queued' && (
                    <Progress percent={entry?.percent ?? 0} size="small" status="active" style={{ marginBottom: 0 }} />
                  )}
                  {status === 'failed' && (
                    <Space size={4}>
                      <Tag color="error">上传失败</Tag>
                      {rawFile ? (
                        <Button size="small" icon={<ReloadOutlined />} onClick={() => retry(rawFile)}>重试</Button>
                      ) : (
                        <>
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            ref={trRetryInputRef}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) retry(f);
                              e.target.value = '';
                            }}
                          />
                          <Button size="small" icon={<ReloadOutlined />} onClick={() => trRetryInputRef.current?.click()}>选择文件重试</Button>
                        </>
                      )}
                    </Space>
                  )}
                  {entry && status !== 'failed' && entry.currentSpeed !== undefined && entry.currentSpeed > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(entry.currentSpeed)}</Text>
                  )}
                  {status === 'uploading' && (!entry?.currentSpeed || entry?.currentSpeed === 0) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>上传中...</Text>
                  )}
                </Space>
              </div>
            );
          };

          return (
            <>
              {renderDocSection(trDocs, content, '测试报告', 'green', pkg.doc_upload_timing?.test_report)}
              {Array.from(pendingRows.entries()).map(([key, pendingName]) => {
                const index = key === prefix ? -1 : Number(key.slice(prefix.length + 1));
                const entry = progress[key];
                const name = entry?.rawFile?.name
                  ?? pendingName
                  ?? (Number.isInteger(index) && index >= 0 ? allTrDocs[index]?.name : pkg.test_report_name);
                const failed = entry ? entry.status === 'failed' : pkg.test_report_upload_status === 'failed';
                return renderProgress(key, name, failed, entry?.rawFile);
              })}
              {legacyStuck && renderProgress(prefix, pkg.test_report_name, true)}
            </>
          );
        })()}
        {!pkg.release_note_url && !pkg.release_note_content &&
          !(pkg.test_report_files?.length) && !pkg.test_report_url && !pkg.test_report_content &&
          !progress[`package-${pkg.id}-doc-release_note`] &&
          !Object.keys(progress).some((k) => k.startsWith(`package-${pkg.id}-doc-test_report`)) &&
          !pkg.release_note_name && !pkg.test_report_name && (
          <Text type="secondary">暂无文档文件</Text>
        )}
      </Card>

      {/* Linked Requirements */}
      <Card title="关联需求" className="mb-4">
        {linkedReqs.length > 0 ? (
          <div>
            {linkedReqs.map((item) => {
              const firstLine = (item.requirement_description || '').split('\n')[0];
              const reqId = item.bvp_requirement_id;
              const projectId = pkg.project?.id;
              return (
                <div key={reqId} className="py-2 border-b border-gray-100 last:border-0">
                  <Space>
                    {projectId ? (
                      <a onClick={() => navigate(`/requirements/${projectId}?reqId=${encodeURIComponent(reqId)}`)}>
                        {reqId}
                      </a>
                    ) : (
                      <Tag>{reqId}</Tag>
                    )}
                    {firstLine && (
                      <Text type="secondary" ellipsis style={{ maxWidth: 500 }}>
                        {firstLine}
                      </Text>
                    )}
                  </Space>
                </div>
              );
            })}
          </div>
        ) : (
          <Text type="secondary">暂无关联需求</Text>
        )}
      </Card>

      {/* Linked OPL Issues */}
      <Card title="关联OPL问题" className="mb-4">
        {linkedIssues.length > 0 ? (
          <div>
            {linkedIssues.map((item) => {
              const projectId = pkg.project?.id;
              return (
                <div key={item.id} className="py-2 border-b border-gray-100 last:border-0">
                  <Space>
                    {projectId ? (
                      <a onClick={() => navigate(`/opl/${projectId}?issueId=${item.id}`)}>
                        {item.issue_id}
                      </a>
                    ) : (
                      <Tag>{item.issue_id}</Tag>
                    )}
                    <IssueStatusTag status={item.status} />
                    {item.priority && <PriorityTag priority={item.priority} />}
                    {item.description_summary && (
                      <Text type="secondary" ellipsis style={{ maxWidth: 500 }}>
                        {item.description_summary}
                      </Text>
                    )}
                  </Space>
                </div>
              );
            })}
          </div>
        ) : (
          <Text type="secondary">暂无关联OPL问题</Text>
        )}
      </Card>

      <div className="flex justify-end">
        <Button onClick={onClose}>关闭</Button>
      </div>
    </div>
  );
};

export default PackageDetail;
